/**
 * @file Reports.js
 * @description Page component for transaction/customer data reporting, analytics summaries, and CSV data exports.
 */

import '../css/pages/Reports.css';
import React, { useState, useEffect, useCallback, useRef } from "react";
import Layout from "../components/Layout";
import DataTable from "react-data-table-component";
import { getCheques, getCustomers } from "../api/services";
import DateInput from "../components/DateInput";
import { formatDate, getChicagoPreset, getTodayChicago } from "../utils/formatDate";

/**
 * Formats numeric value as US currency string.
 * @param {number|string} n - Amount value.
 * @returns {string} Currency representation.
 */
const fmt = (n) =>
  "$" +
  Number(n || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
const fmtDate = (d) => formatDate(d);

const TABS = ["Transactions", "Customers", "Summary"];

/**
 * Converts array of objects to CSV file and triggers download.
 * @param {Array<Object>} rows - Array of objects.
 * @param {string} filename - Output file name.
 */
const exportCSV = (rows, filename) => {
  if (!rows.length) { alert("No data to export"); return; }
  const keys = Object.keys(rows[0]);
  
  const escapeCSV = (val) => {
    let str = String(val ?? "");
    if (str.includes(",") || str.includes('"') || str.includes("\n")) {
      return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
  };

  const csv = [
    keys.join(","),
    ...rows.map((r) => keys.map((k) => escapeCSV(r[k])).join(",")),
  ].join("\n");
  
  const a = document.createElement("a");
  a.href = "data:text/csv;charset=utf-8,\uFEFF" + encodeURIComponent(csv);
  a.download = filename;
  a.click();
};

// ── Date presets — delegates to Chicago-timezone version in formatDate.js ──────
const getPreset = (key) => getChicagoPreset(key);

// ── Shared DataTable styles (matches rest of app) ─────────────────────────────
const DT_STYLES = {
  headRow:    { style: { backgroundColor: "var(--gray-4)", borderBottom: "1px solid var(--gray-3)", minHeight: "42px" } },
  headCells:  { style: { fontSize: "11.5px", fontWeight: "700", color: "var(--gray-1)", textTransform: "uppercase", letterSpacing: "0.6px", paddingLeft: "14px", paddingRight: "10px" } },
  rows:       { style: { fontSize: "13px", color: "var(--dark)", borderBottom: "1px solid var(--gray-4)", minHeight: "48px", "&:hover": { backgroundColor: "rgba(26,122,74,0.04)" } } },
  cells:      { style: { paddingLeft: "14px", paddingRight: "10px" } },
  pagination: {
    style: { borderTop: "1px solid var(--gray-3)", fontSize: "13px", color: "var(--gray-1)", minHeight: "52px" },
    pageButtonsStyle: { borderRadius: "6px", color: "var(--dark)", fill: "var(--gray-1)", "&:hover:not(:disabled)": { backgroundColor: "var(--gray-4)" }, "&:focus": { outline: "none", backgroundColor: "var(--gray-4)" } },
  },
  noData:   { style: { padding: "48px", color: "var(--gray-1)", fontSize: "14px" } },
  progress: { style: { padding: "48px" } },
};

const STATUS_HEX = { Cashed: "#16a34a", Pending: "#d97706", Returned: "#dc2626", Void: "#6b7280", Hold: "#2563eb" };

// ── Transaction columns ────────────────────────────────────────────────────────
const TX_COLUMNS = [
  {
    name: "CUSTOMER", selector: (r) => `${r.customer?.firstName || ""} ${r.customer?.lastName || ""}`.trim(), sortable: true, minWidth: "150px",
    cell: (r) => <strong style={{ fontSize: 13 }}>{r.customer?.firstName} {r.customer?.lastName}</strong>,
  },
  {
    name: "CASHED DATE", selector: (r) => r.dateCashed, sortable: true, sortField: "dateCashed", minWidth: "115px",
    cell: (r) => <span className="td-date" style={{ fontSize: 12, color: "var(--gray-1)", whiteSpace: "nowrap" }}>{fmtDate(r.dateCashed)}</span>,
  },
  {
    name: "CHECK DATE", selector: (r) => r.checkDate, sortable: true, sortField: "checkDate", minWidth: "115px",
    cell: (r) => <span style={{ fontSize: 12, color: "var(--gray-2)", whiteSpace: "nowrap", fontFamily: "monospace" }}>{fmtDate(r.checkDate)}</span>,
  },
  {
    name: "CHECK #", selector: (r) => r.checkNumber, sortable: true, sortField: "checkNumber", minWidth: "115px",
    cell: (r) => <span style={{ fontFamily: "monospace", fontWeight: 600 }}>{r.checkNumber}</span>,
  },
  {
    name: "TYPE", selector: (r) => r.checkType, sortable: true, minWidth: "95px",
    cell: (r) => <span style={{ fontSize: 11, background: "var(--gray-4)", padding: "2px 8px", borderRadius: 10, fontWeight: 600 }}>{r.checkType}</span>,
  },
  {
    name: "MAKER", selector: (r) => r.maker, sortable: true, minWidth: "140px",
    cell: (r) => <span style={{ fontSize: 12, maxWidth: 140, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "block" }} title={r.maker}>{r.maker || "—"}</span>,
  },
  {
    name: "BANK", selector: (r) => r.bank?.name, minWidth: "110px",
    cell: (r) => <span style={{ fontSize: 12 }}>{r.bank?.name || "—"}</span>,
  },
  {
    name: "AMOUNT", selector: (r) => r.amount, sortable: true, sortField: "amount", right: true, minWidth: "100px",
    cell: (r) => <span className="amount">{fmt(r.amount)}</span>,
  },
  {
    name: "FEE", selector: (r) => r.fee, sortable: true, sortField: "fee", right: true, minWidth: "85px",
    cell: (r) => <span style={{ color: "var(--danger)", fontWeight: 600 }}>{fmt(r.fee)}</span>,
  },
  {
    name: "PAYOUT", selector: (r) => r.payOut, sortable: true, sortField: "payOut", right: true, minWidth: "100px",
    cell: (r) => <span className="amount-positive">{fmt(r.payOut)}</span>,
  },
  {
    name: "STATUS", selector: (r) => r.status, sortable: true, sortField: "status", minWidth: "95px",
    cell: (r) => (
      <span style={{ fontSize: 11, fontWeight: 700, color: STATUS_HEX[r.status] || "var(--gray-1)", background: `${STATUS_HEX[r.status] || "#6b7280"}18`, padding: "3px 9px", borderRadius: 10 }}>
        {r.status}
      </span>
    ),
  },
];

// ── Customer columns ───────────────────────────────────────────────────────────
const CUST_COLUMNS = [
  {
    name: "NAME", selector: (r) => `${r.firstName} ${r.lastName}`, sortable: true, minWidth: "160px",
    cell: (r) => <strong style={{ fontSize: 13 }}>{r.firstName} {r.middleName} {r.lastName}</strong>,
  },
  {
    name: "DOB", selector: (r) => r.dateOfBirth, sortable: true, minWidth: "110px",
    cell: (r) => <span style={{ fontSize: 12 }}>{fmtDate(r.dateOfBirth)}</span>,
  },
  {
    name: "ID TYPE", selector: (r) => r.identifications?.[0]?.type, minWidth: "110px",
    cell: (r) => <span style={{ fontSize: 11 }}>{r.identifications?.[0]?.type || "—"}</span>,
  },
  {
    name: "ID NUMBER", selector: (r) => r.identifications?.[0]?.idNumber, minWidth: "130px",
    cell: (r) => <span style={{ fontFamily: "monospace", fontWeight: 600, fontSize: 12 }}>{r.identifications?.[0]?.idNumber || "—"}</span>,
  },
  {
    name: "ID EXPIRY", selector: (r) => r.identifications?.[0]?.expiryDate, sortable: true, minWidth: "115px",
    cell: (r) => {
      const exp = r.identifications?.[0]?.expiryDate;
      const expired = exp && new Date(exp) < new Date();
      return <span style={{ fontSize: 12, color: expired ? "var(--danger)" : "inherit" }}>{fmtDate(exp)}</span>;
    },
  },
  {
    name: "PHONE", selector: (r) => r.contacts?.[0]?.value, minWidth: "130px",
    cell: (r) => <span style={{ fontSize: 12 }}>{r.contacts?.[0]?.value || "—"}</span>,
  },
  {
    name: "CITY / STATE", selector: (r) => r.address?.city, sortable: true, minWidth: "120px",
    cell: (r) => <span style={{ fontSize: 12 }}>{r.address?.city}{r.address?.state ? `, ${r.address.state}` : ""}</span>,
  },
  {
    name: "EMPLOYER", selector: (r) => r.employer?.name, minWidth: "130px",
    cell: (r) => <span style={{ fontSize: 12, maxWidth: 130, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "block" }}>{r.employer?.name || "—"}</span>,
  },
  {
    name: "STATUS", selector: (r) => r.isBlocked, sortable: true, minWidth: "95px",
    cell: (r) => r.isBlocked
      ? <span style={{ fontSize: 11, fontWeight: 700, color: "#dc2626", background: "#fef2f2", padding: "2px 8px", borderRadius: 10 }}>🚫 Blocked</span>
      : <span style={{ fontSize: 11, fontWeight: 600, color: "#16a34a", background: "#f0fdf4", padding: "2px 8px", borderRadius: 10 }}>Active</span>,
  },
  {
    name: "ADDED", selector: (r) => r.createdAt, sortable: true, minWidth: "110px",
    cell: (r) => <span style={{ fontSize: 11, color: "var(--gray-1)" }}>{fmtDate(r.createdAt)}</span>,
  },
];

const CUST_CONDITIONAL_STYLES = [
  { when: (r) => r.isBlocked, style: { backgroundColor: "#fff5f5" } },
];

/**
 * Main Reports page component.
 * @component
 * @returns {React.ReactElement}
 */
export default function Reports() {
  const [tab, setTab] = useState("Transactions");

  // ── Transaction state ──────────────────────────────────────────────────────
  const [txFilters, setTxFilters] = useState({
    dateFrom: "", dateTo: "", dateSearchBy: "createdAt",
    status: "", checkType: "", search: "",
  });
  const [txData,    setTxData]    = useState([]);
  const [txSummary, setTxSummary] = useState(null);
  const [txLoading, setTxLoading] = useState(false);
  const [txPage,    setTxPage]    = useState(1);
  const [txLimit,   setTxLimit]   = useState(20);
  const [txTotal,   setTxTotal]   = useState(0);
  const [txSort,    setTxSort]    = useState({ column: {}, direction: "desc" });

  // ── Customer state ─────────────────────────────────────────────────────────
  const [custFilters, setCustFilters] = useState({ search: "", state: "", isBlocked: "" });
  const [custData,    setCustData]    = useState([]);
  const [custLoading, setCustLoading] = useState(false);
  const [custPage,    setCustPage]    = useState(1);
  const [custLimit,   setCustLimit]   = useState(20);
  const [custTotal,   setCustTotal]   = useState(0);

  // ── Fetch transactions ─────────────────────────────────────────────────────
  // overrideFilters: pass cleared values directly from Clear All so we
  // never depend on stale txFilters state (setState is async).
  const lastFetchTimeRef = useRef(0);
  const lastFetchParamsRef = useRef("");

  const fetchTx = useCallback(async (pg = 1, perPage = txLimit, sortState = txSort, overrideFilters = null) => {
    try {
      const active = overrideFilters !== null ? overrideFilters : txFilters;
      const { dateSearchBy, ...rest } = active;
      const sortField = sortState?.column?.sortField || "dateCashed";
      const sortDir   = sortState?.direction === "asc" ? sortField : `-${sortField}`;
      const params    = { ...rest, page: Number(pg), limit: perPage, sort: sortDir };
      Object.keys(params).forEach((k) => {
        if (k === "page" || k === "limit" || k === "sort") return;
        if (params[k] === "" || params[k] === null || params[k] === undefined) delete params[k];
      });
      if (active.dateFrom || active.dateTo) params.dateField = dateSearchBy || "createdAt";

      // Deduplicate identical requests within 400ms (prevents React StrictMode / multi-trigger duplicate calls)
      const paramsKey = JSON.stringify(params);
      const now = Date.now();
      if (paramsKey === lastFetchParamsRef.current && now - lastFetchTimeRef.current < 400) {
        return;
      }
      lastFetchParamsRef.current = paramsKey;
      lastFetchTimeRef.current = now;

      setTxLoading(true);
      const res = await getCheques(params);
      setTxData(res.data.data || []);
      setTxTotal(res.data.total || 0);
      setTxSummary(res.data.summary || null);
      setTxPage(Number(pg));
    } catch (e) { console.error("fetchTx error", e); }
    finally { setTxLoading(false); }
  }, [txFilters, txLimit, txSort]);

  // ── Fetch customers ────────────────────────────────────────────────────────
  const lastCustTimeRef = useRef(0);
  const lastCustParamsRef = useRef("");

  const fetchCust = useCallback(async (pg = 1, perPage = custLimit, overrideFilters = null) => {
    try {
      const active = overrideFilters !== null ? overrideFilters : custFilters;
      const params = { ...active, page: Number(pg), limit: perPage, sort: "-createdAt" };
      Object.keys(params).forEach((k) => {
        if (k === "page" || k === "limit" || k === "sort") return;
        if (params[k] === "" || params[k] === null || params[k] === undefined) delete params[k];
      });

      // Deduplicate identical requests within 400ms
      const paramsKey = JSON.stringify(params);
      const now = Date.now();
      if (paramsKey === lastCustParamsRef.current && now - lastCustTimeRef.current < 400) {
        return;
      }
      lastCustParamsRef.current = paramsKey;
      lastCustTimeRef.current = now;

      setCustLoading(true);
      const res = await getCustomers(params);
      setCustData(res.data.data || []);
      setCustTotal(res.data.total || 0);
      setCustPage(Number(pg));
    } catch (e) { console.error("fetchCust error", e); }
    finally { setCustLoading(false); }
  }, [custFilters, custLimit]);

  useEffect(() => { if (tab === "Transactions") fetchTx(1); }, [tab]); // eslint-disable-line
  useEffect(() => { if (tab === "Customers")    fetchCust(1); }, [tab]); // eslint-disable-line

  const applyPreset = (key) => {
    const p = getPreset(key);
    setTxFilters((f) => ({ ...f, dateFrom: p.from, dateTo: p.to }));
  };

  // ── Export all tx ──────────────────────────────────────────────────────────
  const exportAllTx = async () => {
    try {
      const { dateSearchBy, ...rest } = txFilters;
      const params = { ...rest, limit: 10000, sort: "-dateCashed" };
      Object.keys(params).forEach((k) => !params[k] && delete params[k]);
      if (txFilters.dateFrom || txFilters.dateTo) params.dateField = dateSearchBy || "createdAt";
      const res  = await getCheques(params);
      const rows = (res.data.data || []).map((c) => ({
        Date: fmtDate(c.dateCashed),
        Customer: `${c.customer?.firstName || ""} ${c.customer?.lastName || ""}`.trim(),
        CheckNumber: c.checkNumber, CheckType: c.checkType,
        Maker: c.maker, Bank: c.bank?.name,
        Amount: c.amount, Fee: c.fee, PayOut: c.payOut, Status: c.status,
        RoutingNumber: c.bank?.routingNumber, AccountNumber: c.bank?.accountNumber,
      }));
      exportCSV(rows, `transactions_${getTodayChicago()}.csv`);
    } catch { alert("Export failed"); }
  };

  // ── Export all customers ───────────────────────────────────────────────────
  const exportAllCust = async () => {
    try {
      const res  = await getCustomers({ limit: 10000, sort: "-createdAt" });
      const rows = (res.data.data || []).map((c) => ({
        FirstName: c.firstName, MiddleName: c.middleName, LastName: c.lastName,
        DOB: fmtDate(c.dateOfBirth), Sex: c.sex, Height: c.height, Weight: c.weight,
        IDType: c.identifications?.[0]?.type, IDNumber: c.identifications?.[0]?.idNumber,
        IDState: c.identifications?.[0]?.issuedState, IDExpiry: fmtDate(c.identifications?.[0]?.expiryDate),
        Street: c.address?.street, City: c.address?.city, State: c.address?.state, ZIP: c.address?.zip,
        Phone: c.contacts?.[0]?.value, Employer: c.employer?.name,
        Blocked: c.isBlocked ? "Yes" : "No", BlockReason: c.blockedReason || "",
        CreatedAt: fmtDate(c.createdAt),
      }));
      exportCSV(rows, `customers_${getTodayChicago()}.csv`);
    } catch { alert("Export failed"); }
  };

  // ── DataTable server-side handlers: Transactions ───────────────────────────
  const handleTxSort = (column, direction) => {
    const newSort = { column, direction };
    setTxSort(newSort);
    setTxPage(1);
    fetchTx(1, txLimit, newSort);
  };
  const handleTxPageChange       = (pg)               => { setTxPage(pg);    fetchTx(pg, txLimit, txSort); };
  const handleTxRowsPerPage      = (newLimit, pg)     => { setTxLimit(newLimit); setTxPage(pg); fetchTx(pg, newLimit, txSort); };

  // ── DataTable server-side handlers: Customers ──────────────────────────────
  const handleCustPageChange     = (pg)               => { setCustPage(pg);  fetchCust(pg, custLimit); };
  const handleCustRowsPerPage    = (newLimit, pg)     => { setCustLimit(newLimit); setCustPage(pg); fetchCust(pg, newLimit); };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <Layout title="Reports">
      {/* Page header */}
      <div style={{ marginBottom: 20, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <h2 style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 22 }}>📈 Reports</h2>
          <p style={{ color: "var(--gray-1)", fontSize: 13, marginTop: 3 }}>Filter, view and export transaction & customer data.</p>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", gap: 4, marginBottom: 20, background: "var(--gray-4)", padding: 4, borderRadius: 10, width: "fit-content" }}>
        {TABS.map((t) => (
          <button key={t} onClick={() => setTab(t)}
            style={{ padding: "8px 20px", borderRadius: 8, border: "none", cursor: "pointer", fontWeight: 700, fontSize: 13,
              background: tab === t ? "white" : "transparent",
              color:      tab === t ? "var(--primary)" : "var(--gray-1)",
              boxShadow:  tab === t ? "0 1px 4px rgba(0,0,0,0.1)" : "none",
              transition: "all 0.2s" }}>
            {t === "Transactions" ? "📋 Transactions" : t === "Customers" ? "👥 Customers" : "📊 Summary"}
          </button>
        ))}
      </div>

      {/* ══════════════════════ TRANSACTIONS TAB ══════════════════════ */}
      {tab === "Transactions" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

          {/* ── Filter card ── */}
          <div className="card">
            <div className="card-body">
              <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 12, color: "var(--gray-1)", textTransform: "uppercase", letterSpacing: 0.5 }}>
                🔍 Filters
              </div>

              {/* Date preset pills */}
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 12 }}>
                {[["today","Today"],["yesterday","Yesterday"],["this_week","This Week"],["last_week","Last Week"],
                  ["this_month","This Month"],["last_month","Last Month"],["this_year","This Year"],["last_year","Last Year"]].map(([k, l]) => (
                  <button key={k} onClick={() => applyPreset(k)}
                    style={{ padding: "5px 12px", borderRadius: 20, border: "1.5px solid var(--gray-3)", background: "white", fontSize: 12, cursor: "pointer", fontWeight: 600, color: "var(--dark)" }}>
                    {l}
                  </button>
                ))}
                <button onClick={() => setTxFilters((f) => ({ ...f, dateFrom: "", dateTo: "" }))}
                  style={{ padding: "5px 12px", borderRadius: 20, border: "1.5px solid var(--gray-3)", background: "white", fontSize: 12, cursor: "pointer", color: "var(--gray-1)" }}>
                  Clear Dates
                </button>
              </div>

              {/* Filter fields */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 10 }}>
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label">Date From</label>
                  <DateInput value={txFilters.dateFrom} onChange={(e) => setTxFilters((f) => ({ ...f, dateFrom: e.target.value }))} />
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label">Date To</label>
                  <DateInput value={txFilters.dateTo} onChange={(e) => setTxFilters((f) => ({ ...f, dateTo: e.target.value }))} />
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label">Search By Date</label>
                  <select className="form-control form-select" value={txFilters.dateSearchBy}
                    onChange={(e) => setTxFilters((f) => ({ ...f, dateSearchBy: e.target.value }))}>
                    <option value="createdAt">Cashed Date</option>
                    <option value="dateCashed">Check Date</option>
                  </select>
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label">Status</label>
                  <select className="form-control form-select" value={txFilters.status}
                    onChange={(e) => setTxFilters((f) => ({ ...f, status: e.target.value }))}>
                    <option value="">All</option>
                    {["Cashed","Pending","Hold","Returned","Void"].map((s) => <option key={s}>{s}</option>)}
                  </select>
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label">Check Type</label>
                  <select className="form-control form-select" value={txFilters.checkType}
                    onChange={(e) => setTxFilters((f) => ({ ...f, checkType: e.target.value }))}>
                    <option value="">All</option>
                    {["Personal","Business","Payroll","Government","Treasury","Cashier"].map((t) => <option key={t}>{t}</option>)}
                  </select>
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label">Search</label>
                  <input className="form-control" value={txFilters.search}
                    onChange={(e) => setTxFilters((f) => ({ ...f, search: e.target.value }))}
                    onKeyDown={(e) => e.key === "Enter" && fetchTx(1)}
                    placeholder="Check #, Maker, Bank…" />
                </div>
              </div>

              {/* Action buttons */}
              <div style={{ display: "flex", gap: 10, marginTop: 12 }}>
                <button className="btn btn-primary btn-sm" onClick={() => fetchTx(1)}>🔍 Apply Filters</button>
                <button className="btn btn-outline btn-sm"
                  onClick={() => {
                    const empty = { dateFrom: "", dateTo: "", dateSearchBy: "createdAt", status: "", checkType: "", search: "" };
                    setTxFilters(empty);
                    setTxPage(1);
                    fetchTx(1, txLimit, txSort, empty);
                  }}>
                  ✕ Clear All
                </button>
                <button className="btn btn-outline btn-sm"
                  style={{ marginLeft: "auto", color: "var(--primary)", fontWeight: 700 }}
                  onClick={exportAllTx}>
                  ⬇ Export CSV
                </button>
              </div>
            </div>
          </div>

          {/* ── Summary bar ── */}
          {txSummary && (
            <div className="reports-summary-grid" style={{ display: "grid", gap: 12 }}>
              {[
                { label: "Total Volume",  val: fmt(txSummary.totalAmount),  color: "var(--primary)" },
                { label: "Total Fees",    val: fmt(txSummary.totalFee),     color: "var(--accent)"  },
                { label: "Total Payout",  val: fmt(txSummary.totalPayout),  color: "var(--success)" },
                { label: "Transactions",  val: txTotal.toLocaleString(),    color: "var(--info)"    },
              ].map((s) => (
                <div key={s.label} className="card" style={{ margin: 0 }}>
                  <div className="card-body" style={{ padding: "12px 16px" }}>
                    <div style={{ fontSize: 11, color: "var(--gray-1)", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 }}>{s.label}</div>
                    <div style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 18, color: s.color }}>{s.val}</div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* ── DataTable ── */}
          <div className="card">
            <DataTable
              columns={TX_COLUMNS}
              data={txData}
              progressPending={txLoading}
              progressComponent={
                <div style={{ padding: 48, textAlign: "center" }}>
                  <div className="spinner spinner-dark" style={{ margin: "0 auto" }} />
                </div>
              }
              noDataComponent={
                <div style={{ padding: 48, textAlign: "center", color: "var(--gray-1)", fontSize: 14 }}>
                  No transactions found. Adjust filters and click Apply.
                </div>
              }
              pagination
              paginationServer
              paginationTotalRows={txTotal}
              paginationDefaultPage={txPage}
              paginationPerPage={txLimit}
              paginationRowsPerPageOptions={[10, 20, 50, 100]}
              onChangePage={handleTxPageChange}
              onChangeRowsPerPage={handleTxRowsPerPage}
              sortServer
              onSort={handleTxSort}
              highlightOnHover
              customStyles={DT_STYLES}
            />
          </div>
        </div>
      )}

      {/* ══════════════════════ CUSTOMERS TAB ══════════════════════ */}
      {tab === "Customers" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

          {/* ── Filter card ── */}
          <div className="card">
            <div className="card-body">
              <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 12, color: "var(--gray-1)", textTransform: "uppercase", letterSpacing: 0.5 }}>
                🔍 Filters
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 10 }}>
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label">Search</label>
                  <input className="form-control" value={custFilters.search}
                    onChange={(e) => setCustFilters((f) => ({ ...f, search: e.target.value }))}
                    onKeyDown={(e) => e.key === "Enter" && fetchCust(1)}
                    placeholder="Name, ID, phone…" />
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label">State</label>
                  <select className="form-control form-select" value={custFilters.state}
                    onChange={(e) => setCustFilters((f) => ({ ...f, state: e.target.value }))}>
                    <option value="">All States</option>
                    {["AL","FL","GA","MS","TN","TX"].map((s) => <option key={s}>{s}</option>)}
                  </select>
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label">Status</label>
                  <select className="form-control form-select" value={custFilters.isBlocked}
                    onChange={(e) => setCustFilters((f) => ({ ...f, isBlocked: e.target.value }))}>
                    <option value="">All</option>
                    <option value="false">Active</option>
                    <option value="true">Blocked</option>
                  </select>
                </div>
              </div>
              <div style={{ display: "flex", gap: 10, marginTop: 12 }}>
                <button className="btn btn-primary btn-sm" onClick={() => fetchCust(1)}>🔍 Apply</button>
                <button className="btn btn-outline btn-sm" onClick={() => {
                    const empty = { search: "", state: "", isBlocked: "" };
                    setCustFilters(empty);
                    setCustPage(1);
                    fetchCust(1, custLimit, empty);
                  }}>✕ Clear</button>
                <button className="btn btn-outline btn-sm"
                  style={{ marginLeft: "auto", color: "var(--primary)", fontWeight: 700 }}
                  onClick={exportAllCust}>
                  ⬇ Export CSV
                </button>
              </div>
            </div>
          </div>

          {/* ── DataTable ── */}
          <div className="card">
            <DataTable
              columns={CUST_COLUMNS}
              data={custData}
              progressPending={custLoading}
              progressComponent={
                <div style={{ padding: 48, textAlign: "center" }}>
                  <div className="spinner spinner-dark" style={{ margin: "0 auto" }} />
                </div>
              }
              noDataComponent={
                <div style={{ padding: 48, textAlign: "center", color: "var(--gray-1)", fontSize: 14 }}>
                  No customers found. Adjust filters and click Apply.
                </div>
              }
              pagination
              paginationServer
              paginationTotalRows={custTotal}
              paginationDefaultPage={custPage}
              paginationPerPage={custLimit}
              paginationRowsPerPageOptions={[10, 20, 50, 100]}
              onChangePage={handleCustPageChange}
              onChangeRowsPerPage={handleCustRowsPerPage}
              highlightOnHover
              customStyles={DT_STYLES}
              conditionalRowStyles={CUST_CONDITIONAL_STYLES}
            />
          </div>
        </div>
      )}

      {/* ══════════════════════ SUMMARY TAB ══════════════════════ */}
      {tab === "Summary" && <SummaryTab />}
    </Layout>
  );
}

// ── SummaryTab — unchanged, kept exactly as original ──────────────────────────
function SummaryTab() {
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo,   setDateTo]   = useState("");
  const [data,     setData]     = useState(null);
  const [loading,  setLoading]  = useState(false);

  const applyPreset = (key) => {
    const p = getPreset(key);
    setDateFrom(p.from);
    setDateTo(p.to);
  };

  const fetchSummary = async () => {
    setLoading(true);
    try {
      const params = { limit: 10000, sort: "-dateCashed" };
      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo)   params.dateTo   = dateTo;
      const res    = await getCheques(params);
      const checks = res.data.data || [];
      const summary = res.data.summary || {};
      const byType = {}, byStatus = {}, byDay = {};
      checks.forEach((c) => {
        const type = c.checkType || "Other";
        if (!byType[type]) byType[type] = { count: 0, amount: 0, fee: 0 };
        byType[type].count++; byType[type].amount += c.amount; byType[type].fee += c.fee;
        const st = c.status || "Unknown";
        if (!byStatus[st]) byStatus[st] = { count: 0, amount: 0 };
        byStatus[st].count++; byStatus[st].amount += c.amount;
        const day = formatDate(c.dateCashed);
        if (!byDay[day]) byDay[day] = { count: 0, amount: 0, fee: 0 };
        byDay[day].count++; byDay[day].amount += c.amount; byDay[day].fee += c.fee;
      });
      setData({ summary, byType, byStatus, byDay, total: res.data.total });
    } catch {
    } finally { setLoading(false); }
  };

  const fmt = (n) =>
    "$" + Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div className="card">
        <div className="card-body">
          <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 12, color: "var(--gray-1)", textTransform: "uppercase", letterSpacing: 0.5 }}>📅 Date Range</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 12 }}>
            {[["this_month","This Month"],["last_month","Last Month"],["this_year","This Year"],["last_year","Last Year"]].map(([k, l]) => (
              <button key={k} onClick={() => applyPreset(k)}
                style={{ padding: "5px 12px", borderRadius: 20, border: "1.5px solid var(--gray-3)", background: "white", fontSize: 12, cursor: "pointer", fontWeight: 600 }}>
                {l}
              </button>
            ))}
          </div>
          <div style={{ display: "flex", gap: 12, alignItems: "flex-end" }}>
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">From</label>
              <DateInput value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">To</label>
              <DateInput value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
            </div>
            <button className="btn btn-primary" onClick={fetchSummary} disabled={loading}>
              {loading ? "Loading…" : "📊 Generate Summary"}
            </button>
          </div>
        </div>
      </div>

      {data && (
        <>
          {/* Totals */}
          <div className="reports-summary-grid" style={{ display: "grid", gap: 12 }}>
            {[
              { label: "Total Volume",  val: fmt(data.summary.totalAmount), color: "var(--primary)" },
              { label: "Total Fees",    val: fmt(data.summary.totalFee),    color: "var(--accent)"  },
              { label: "Total Payout",  val: fmt(data.summary.totalPayout), color: "var(--success)" },
              { label: "Transactions",  val: data.total,                    color: "var(--info)"    },
            ].map((s) => (
              <div key={s.label} className="card" style={{ margin: 0 }}>
                <div className="card-body" style={{ padding: "14px 18px" }}>
                  <div style={{ fontSize: 11, color: "var(--gray-1)", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 }}>{s.label}</div>
                  <div style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 22, color: s.color }}>{s.val}</div>
                </div>
              </div>
            ))}
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            {/* By Check Type */}
            <div className="card">
              <div className="card-header"><h3>📋 By Check Type</h3></div>
              <div className="card-body">
                <table className="table" style={{ fontSize: 13 }}>
                  <thead><tr><th>Type</th><th>Count</th><th>Volume</th><th>Fees</th></tr></thead>
                  <tbody>
                    {Object.entries(data.byType).sort((a, b) => b[1].amount - a[1].amount).map(([type, s]) => (
                      <tr key={type}>
                        <td><span style={{ fontSize: 11, background: "var(--gray-4)", padding: "2px 8px", borderRadius: 10, fontWeight: 600 }}>{type}</span></td>
                        <td>{s.count}</td>
                        <td className="amount">{fmt(s.amount)}</td>
                        <td style={{ color: "var(--accent)" }}>{fmt(s.fee)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* By Status */}
            <div className="card">
              <div className="card-header"><h3>📊 By Status</h3></div>
              <div className="card-body">
                <table className="table" style={{ fontSize: 13 }}>
                  <thead><tr><th>Status</th><th>Count</th><th>Volume</th></tr></thead>
                  <tbody>
                    {Object.entries(data.byStatus).sort((a, b) => b[1].count - a[1].count).map(([st, s]) => {
                      const hex = { Cashed:"#16a34a", Pending:"#d97706", Returned:"#dc2626", Void:"#6b7280", Hold:"#2563eb" }[st] || "#6b7280";
                      return (
                        <tr key={st}>
                          <td><span style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 10, background: `${hex}18`, color: hex }}>{st}</span></td>
                          <td>{s.count}</td>
                          <td className="amount">{fmt(s.amount)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Daily breakdown */}
          <div className="card">
            <div className="card-header"><h3>📅 Daily Breakdown</h3></div>
            <div style={{ overflowX: "auto" }}>
              <table className="table" style={{ fontSize: 13 }}>
                <thead><tr><th>Date</th><th>Transactions</th><th>Volume</th><th>Fees</th><th>Avg Check</th></tr></thead>
                <tbody>
                  {Object.entries(data.byDay).sort((a, b) => new Date(b[0]) - new Date(a[0])).slice(0, 60).map(([day, s]) => (
                    <tr key={day}>
                      <td style={{ fontFamily: "monospace", fontWeight: 600 }}>{day}</td>
                      <td>{s.count}</td>
                      <td className="amount">{fmt(s.amount)}</td>
                      <td style={{ color: "var(--accent)" }}>{fmt(s.fee)}</td>
                      <td style={{ color: "var(--gray-1)" }}>{fmt(s.count ? s.amount / s.count : 0)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
